import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Gift, Loader2, Sparkles, Archive, Pencil, IceCream,
          Popcorn, Palette, Bike, Trees, BookOpen, Puzzle, Ticket, Cookie, Trophy,
          Waves } from "lucide-react";

// ------------------------------------------------------------------
// 10 standard wishes. one-tap add. Points tuned to the Sunday-session
// economy: 45 pts/complete session → 100 pts ≈ 2 weeks, 250 pts ≈ ~5–6
// weeks, 500 pts ≈ full monthly milestone reward.
// ------------------------------------------------------------------
const STANDARD_WISHES = [
  { key: "icecream",  icon: IceCream, name: "Ice Cream Treat",         tier: "small",  pts: 100, color: "text-[hsl(var(--neu-pink))]" },
  { key: "snack",     icon: Cookie,   name: "Favorite Snack Choice",   tier: "small",  pts: 100, color: "text-[hsl(var(--neu-orange))]" },
  { key: "screen",    icon: Ticket,   name: "Extra Screen Time (30m)", tier: "small",  pts: 100, color: "text-[hsl(var(--neu-blue))]" },
  { key: "movie",     icon: Popcorn,  name: "Family Movie Night",      tier: "medium", pts: 250, color: "text-[hsl(var(--neu-orange))]" },
  { key: "book",      icon: BookOpen, name: "Book of Choice",          tier: "medium", pts: 250, color: "text-[hsl(var(--neu-green))]" },
  { key: "boardgame", icon: Puzzle,   name: "New Board Game",          tier: "medium", pts: 250, color: "text-[hsl(var(--neu-blue))]" },
  { key: "art",       icon: Palette,  name: "Art & Craft Kit",         tier: "medium", pts: 250, color: "text-[hsl(var(--neu-pink))]" },
  { key: "outing",    icon: Trees,    name: "Park / Zoo Outing",       tier: "large",  pts: 500, color: "text-[hsl(var(--neu-green))]" },
  { key: "waterpark", icon: Waves,    name: "Water Park Visit",        tier: "large",  pts: 500, color: "text-[hsl(var(--neu-blue))]" },
  { key: "bike",      icon: Bike,     name: "New Bicycle",             tier: "large",  pts: 500, color: "text-[hsl(var(--neu-orange))]" },
];

function Ring({ value, total, size = 56 }) {
  const pct = Math.min(100, Math.round((value / Math.max(total, 1)) * 100));
  const r = size / 2 - 4;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  return (
    <svg width={size} height={size} className="shrink-0">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth="4"/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="hsl(var(--neu-orange))" strokeWidth="4"
               strokeDasharray={`${dash} ${c}`} strokeLinecap="round"
               transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x="50%" y="52%" textAnchor="middle" fontSize="12" fontWeight="600"
             fill="hsl(var(--neu-ink))" dominantBaseline="middle">{pct}%</text>
    </svg>
  );
}

function AddWishSheet({ existingNames, onCreated, onClose }) {
  const [addingKey, setAddingKey] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customTier, setCustomTier] = useState("small");
  const [customPts, setCustomPts] = useState(100);
  const [busy, setBusy] = useState(false);

  const addPreset = async (w) => {
    setAddingKey(w.key);
    try {
      await api.post("/wishes", { name: w.name, tier: w.tier, points_required: w.pts });
      toast.success(`Added: ${w.name}`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add");
      setAddingKey(null);
    }
  };

  const submitCustom = async (e) => {
    e.preventDefault();
    if (!customName.trim()) return;
    setBusy(true);
    try {
      await api.post("/wishes", {
        name: customName.trim(), tier: customTier,
        points_required: Math.max(10, parseInt(customPts, 10) || 100),
      });
      toast.success("Custom wish added");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 fade-in"
         data-testid="wish-add-sheet" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="card-elevated w-full max-w-md p-5 rise-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-baseline justify-between mb-1">
          <h2 className="font-display text-2xl">Pick a wish</h2>
          <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground"
                  data-testid="wish-close-btn">Close</button>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Tap to add. These are the rewards your child earns by showing up each Sunday.
        </p>

        <div className="grid grid-cols-2 gap-2.5">
          {STANDARD_WISHES.map((w) => {
            const alreadyAdded = existingNames.has(w.name.toLowerCase());
            const Icon = w.icon;
            return (
              <button
                key={w.key}
                onClick={() => !alreadyAdded && addPreset(w)}
                disabled={alreadyAdded || addingKey === w.key}
                className={`text-left p-3 rounded-2xl border transition-all ${
                  alreadyAdded
                    ? "opacity-40 border-border cursor-not-allowed"
                    : "border-border hover:border-[hsl(var(--neu-orange))]/50 hover:bg-[hsl(var(--neu-orange))]/5"
                }`}
                data-testid={`wish-preset-${w.key}`}
              >
                <Icon className={`w-6 h-6 ${w.color}`}/>
                <div className="font-medium text-sm mt-2 leading-tight">{w.name}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {alreadyAdded ? "Already added" : `${w.pts} pts · ${w.tier}`}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-5 pt-5 border-t border-border">
          {!customOpen ? (
            <button
              onClick={() => setCustomOpen(true)}
              className="w-full text-left p-4 rounded-2xl border-2 border-dashed border-[hsl(var(--neu-orange))]/30 hover:border-[hsl(var(--neu-orange))]/60 hover:bg-[hsl(var(--neu-orange))]/5 transition-all"
              data-testid="wish-custom-open-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[hsl(var(--neu-orange))]/10 flex items-center justify-center">
                  <Pencil className="w-5 h-5 text-[hsl(var(--neu-orange))]" />
                </div>
                <div>
                  <div className="font-medium">Create a custom wish</div>
                  <div className="text-xs text-muted-foreground">Your own reward · pick the credits</div>
                </div>
              </div>
            </button>
          ) : (
            <form onSubmit={submitCustom} data-testid="wish-custom-form">
              <div className="flex items-baseline justify-between mb-3">
                <div className="text-sm font-medium">Custom wish</div>
                <button type="button" onClick={() => setCustomOpen(false)}
                        className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
              </div>
              <input required autoFocus value={customName}
                     onChange={(e) => setCustomName(e.target.value)}
                     placeholder="e.g. Trip to grandma's this vacation"
                     className="field mb-3"
                     data-testid="wish-custom-name" />
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  { v: "small", label: "Small", pts: 100 },
                  { v: "medium", label: "Medium", pts: 250 },
                  { v: "large", label: "Large", pts: 500 },
                ].map((t) => (
                  <button type="button" key={t.v}
                          onClick={() => { setCustomTier(t.v); setCustomPts(t.pts); }}
                          className={`p-2.5 rounded-xl border text-center transition-all ${
                            customTier === t.v
                              ? "bg-[hsl(var(--neu-orange))]/10 border-[hsl(var(--neu-orange))]"
                              : "border-border"
                          }`}
                          data-testid={`wish-custom-tier-${t.v}`}>
                    <div className="font-display text-base">{t.label}</div>
                    <div className="text-[10px] text-muted-foreground">{t.pts} pts</div>
                  </button>
                ))}
              </div>
              <label className="text-xs text-muted-foreground">Credits required</label>
              <input type="number" min={10} max={5000} step={10} value={customPts}
                     onChange={(e) => setCustomPts(parseInt(e.target.value || "0", 10))}
                     className="field mt-1 mb-4" data-testid="wish-custom-points" />
              <button disabled={busy || !customName.trim()}
                      className="btn-primary w-full"
                      data-testid="wish-custom-save-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin"/>
                       : <><Trophy className="w-4 h-4"/> Add custom wish</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Wishes() {
  const nav = useNavigate();
  const [wishes, setWishes] = useState([]);
  const [balance, setBalance] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/wishes");
      setWishes(r.data.wishes);
      setBalance(r.data.balance);
    } catch { toast.error("Could not load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const redeem = async (id) => {
    if (!window.confirm("Redeem this wish?")) return;
    try {
      const r = await api.post(`/wishes/${id}/redeem`);
      toast.success("🎉 Time to fulfill the promise!");
      setBalance(r.data.new_balance);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not redeem");
    }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this wish?")) return;
    try { await api.post(`/wishes/${id}/archive`); load(); }
    catch { toast.error("Could not archive"); }
  };

  const active = wishes.filter((w) => w.status === "active");
  const redeemed = wishes.filter((w) => w.status === "redeemed");
  const activeNames = new Set(active.map((w) => (w.name || "").toLowerCase()));

  return (
    <div className="min-h-screen pb-24" data-testid="wishes-screen">
      <div className="max-w-md mx-auto px-6 pt-8">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => nav(-1)} className="p-2 rounded-full hover:bg-white/70"
                  data-testid="wishes-back">
            <ArrowLeft className="w-5 h-5"/>
          </button>
          <button onClick={() => setShowAdd(true)} className="btn-primary px-4 py-2"
                  data-testid="wishes-add-btn">
            <Plus className="w-4 h-4"/> Add wish
          </button>
        </div>

        <div className="card-soft p-6 mb-6 flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--neu-pink))]/10 flex items-center justify-center">
            <Gift className="w-7 h-7 text-[hsl(var(--neu-pink))]"/>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Credits earned</div>
            <div className="font-display text-3xl">{balance}</div>
          </div>
        </div>

        <h2 className="font-display text-2xl mb-3">Active wishes</h2>
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground"/>
        ) : active.length === 0 ? (
          <div className="card-soft p-6 text-center" data-testid="wishes-empty">
            <Sparkles className="w-7 h-7 mx-auto text-[hsl(var(--neu-orange))]"/>
            <p className="font-display text-xl mt-3">Pick your child's first wish</p>
            <p className="text-sm text-muted-foreground mt-1">
              Choose from 10 ready-made rewards or create your own.
            </p>
            <button onClick={() => setShowAdd(true)} className="btn-primary mt-4"
                    data-testid="wishes-empty-add-btn">
              <Plus className="w-4 h-4"/> Pick a wish
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {active.map((w) => {
              const canRedeem = balance >= w.points_required;
              return (
                <div key={w.id} className="card-soft p-4 flex items-center gap-4"
                     data-testid={`wish-card-${w.id}`}>
                  <Ring value={Math.min(balance, w.points_required)} total={w.points_required}/>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg truncate">{w.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {w.tier} · {Math.min(balance, w.points_required)} / {w.points_required} credits
                    </div>
                  </div>
                  {canRedeem ? (
                    <button onClick={() => redeem(w.id)} className="btn-primary px-4 py-2 text-sm"
                            data-testid={`wish-redeem-${w.id}`}>Redeem</button>
                  ) : (
                    <button onClick={() => archive(w.id)} className="p-2 text-muted-foreground hover:bg-muted rounded-full"
                            data-testid={`wish-archive-${w.id}`}>
                      <Archive className="w-4 h-4"/>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {redeemed.length > 0 && (
          <>
            <h2 className="font-display text-2xl mb-3 mt-8">Redeemed</h2>
            <div className="space-y-3">
              {redeemed.map((w) => (
                <div key={w.id} className="card-soft p-4 flex items-center gap-3 opacity-70">
                  <Gift className="w-5 h-5 text-[hsl(var(--neu-pink))]"/>
                  <div className="flex-1">
                    <div className="font-medium">{w.name}</div>
                    <div className="text-xs text-muted-foreground">{w.points_required} credits · redeemed</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {showAdd && (
        <AddWishSheet
          existingNames={activeNames}
          onCreated={() => { setShowAdd(false); load(); }}
          onClose={() => setShowAdd(false)}
        />
      )}
    </div>
  );
}
